import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calculator, Info, TrendingUp, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/**
 * PriceEstimation Component
 * Shows an orientation price based on area, category, region, and tree type
 */
export const PriceEstimation = ({ 
  flaeche_ha, 
  kategorie, 
  bundesland, 
  baumart = null,
  compact = false 
}) => {
  const [estimation, setEstimation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEstimation = async () => {
      // Need at least area, category, and state
      if (!flaeche_ha || !kategorie || !bundesland || flaeche_ha <= 0) {
        setEstimation(null);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const response = await api.post('/api/tools/price-estimation', {
          flaeche_ha: parseFloat(flaeche_ha),
          kategorie,
          bundesland,
          baumart: baumart || null
        });
        setEstimation(response.data);
      } catch (err) {
        console.error('Price estimation error:', err);
        setError('Preisschätzung nicht verfügbar');
        setEstimation(null);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(fetchEstimation, 500);
    return () => clearTimeout(timeoutId);
  }, [flaeche_ha, kategorie, bundesland, baumart]);

  // Don't render if no data
  if (!flaeche_ha || !kategorie || !bundesland) {
    return null;
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  if (compact) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="price-estimation-compact">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100">
            <Calculator className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 mb-1">Geschätzter Orientierungspreis</p>
            {loading ? (
              <div className="animate-pulse h-6 w-32 bg-amber-200 rounded"></div>
            ) : error ? (
              <p className="text-sm text-amber-700">{error}</p>
            ) : estimation ? (
              <>
                <p className="text-lg font-bold text-amber-900">
                  {formatPrice(estimation.min_price)} – {formatPrice(estimation.max_price)}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  ca. {formatPrice(estimation.factors_applied.preis_pro_ha_min)} – {formatPrice(estimation.factors_applied.preis_pro_ha_max)} pro Hektar
                </p>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50" data-testid="price-estimation-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-amber-900">
          <Calculator className="h-5 w-5" />
          Kostenorientierung
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-2">
            <div className="animate-pulse h-8 w-48 bg-amber-200 rounded"></div>
            <div className="animate-pulse h-4 w-32 bg-amber-100 rounded"></div>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        ) : estimation ? (
          <>
            <div>
              <p className="text-sm text-amber-700 mb-1">Geschätzter Preisbereich</p>
              <p className="text-2xl font-bold text-amber-900">
                {formatPrice(estimation.min_price)} – {formatPrice(estimation.max_price)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-white/60 rounded-lg">
                <p className="text-amber-600 text-xs mb-1">Preis pro Hektar</p>
                <p className="font-semibold text-amber-900">
                  {formatPrice(estimation.factors_applied.preis_pro_ha_min)} – {formatPrice(estimation.factors_applied.preis_pro_ha_max)}
                </p>
              </div>
              <div className="p-3 bg-white/60 rounded-lg">
                <p className="text-amber-600 text-xs mb-1">Berechnete Fläche</p>
                <p className="font-semibold text-amber-900">
                  {estimation.factors_applied.flaeche_ha} ha
                </p>
              </div>
            </div>

            {estimation.factors_applied.tree_factor !== 1.0 || estimation.factors_applied.regional_factor !== 1.0 ? (
              <div className="flex items-start gap-2 p-3 bg-white/60 rounded-lg">
                <TrendingUp className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-xs text-amber-700">
                  <p className="font-medium mb-1">Angewendete Faktoren:</p>
                  <ul className="space-y-0.5">
                    {estimation.factors_applied.tree_factor !== 1.0 && (
                      <li>Baumart-Faktor: {(estimation.factors_applied.tree_factor * 100).toFixed(0)}%</li>
                    )}
                    {estimation.factors_applied.regional_factor !== 1.0 && (
                      <li>Regional-Faktor: {(estimation.factors_applied.regional_factor * 100).toFixed(0)}%</li>
                    )}
                  </ul>
                </div>
              </div>
            ) : null}

            <div className="flex items-start gap-2 p-3 bg-amber-100/50 rounded-lg border border-amber-200">
              <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                {estimation.disclaimer}
              </p>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PriceEstimation;
